/**
 * In-memory session token store (ADR-20261005-h9r9; TG-35 standard "JWT in
 * memory only"). Never written to localStorage, sessionStorage or a
 * cookie this module can read -- only the HttpOnly `__Host-sbr_refresh`
 * cookie survives a reload, and that cookie is set and read by the
 * server alone.
 *
 * AC-9 (human decision: refresh only on use): this module schedules no
 * timer and makes no refresh request of its own. A proactive refresh
 * would restart the server's idle window while the customer is idle, so
 * an idle tab would stay signed in until the absolute window. Refresh is
 * purely reactive: authenticated-fetch.ts's `authenticatedFetch` refreshes
 * on a 401 and retries, and (ADR-20261016-r4bn, TG-8) a guarded route
 * mounting with no in-memory token counts as "use" too -- App.tsx's
 * `RequireSession` calls `bootstrapSessionFromRefreshCookie`
 * (authenticated-fetch.ts) exactly once at mount in that case, sharing the
 * same single-flight sequence as a reactive 401 refresh. Neither path
 * runs on a timer or while the customer is merely idle with a route
 * already mounted.
 */

export type SessionEndedHandler = () => void;

let currentToken: string | null = null;
let sessionEndedHandler: SessionEndedHandler | null = null;

/**
 * ADR-20261008-jg8z (TG-44): the client-boundary delivery mechanism for
 * AC-9 "session ended". Registered once by App.tsx with a callback that
 * navigates to /sign-in/link-unavailable -- authenticated-fetch.ts calls
 * it, via `notifySessionEnded` below, whenever a reactive refresh
 * definitively fails (ADR-20261015-6576: the refresh endpoint's own 401,
 * never a transient 5xx/429/network failure). A second registration
 * replaces the first -- there is exactly one handler.
 */
export function registerSessionEndedHandler(handler: SessionEndedHandler): void {
  sessionEndedHandler = handler;
}

/** Invokes the registered session-ended handler, if any. This module is
 * the one stateful registry; other portal-auth modules only call into it
 * (THR-26 / no-circular). A no-op before App.tsx has registered a
 * handler. */
export function notifySessionEnded(): void {
  sessionEndedHandler?.();
}

export function getSessionToken(): string | null {
  return currentToken;
}

export type TokenChangeListener = () => void;

const tokenChangeListeners = new Set<TokenChangeListener>();

/**
 * ADR-20260821-m1sn (TG-3): the one subscription point for "the session
 * token just changed" -- App.tsx's composition root uses this (via
 * `capability/session-claim-source.ts`) to re-render with the current
 * claim whenever sign-in, redeem, refresh or sign-out changes which token
 * (if any) this module holds, through `useSyncExternalStore` rather than
 * a prop or a context this module has no reason to own. Returns an
 * unsubscribe function, the same shape `useSyncExternalStore`'s own
 * `subscribe` parameter expects.
 */
export function subscribeTokenChange(listener: TokenChangeListener): () => void {
  tokenChangeListeners.add(listener);
  return () => {
    tokenChangeListeners.delete(listener);
  };
}

function notifyTokenChanged(): void {
  for (const listener of tokenChangeListeners) {
    listener();
  }
}

/** Sets (or clears, with `null`) the in-memory session token. */
export function setSessionToken(token: string | null): void {
  currentToken = token;
  notifyTokenChanged();
}

export function clearSession(): void {
  setSessionToken(null);
}

/**
 * Decodes a JWT's middle (payload) segment with no signature verification
 * -- this module never trusts anything it reads back from it; the server
 * has already verified the token before issuing or accepting it, and
 * every value read here is used only to decide what the client renders or
 * which sessionStorage key to use, never to authorise a write (AC-24,
 * AC-26 stay server-enforced). Returns null for anything that is not a
 * three-part, base64url-JSON-decodable string whose decoded value is a
 * plain object.
 */
function decodeJwtPayloadUncached(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  const payloadSegment = parts[1];
  if (parts.length !== 3 || payloadSegment === undefined) {
    return null;
  }
  try {
    const base64 = payloadSegment.replaceAll("-", "+").replaceAll("_", "/");
    const padding = (4 - (base64.length % 4)) % 4;
    const json = atob(base64 + "=".repeat(padding));
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

// `getSessionClaim` feeds `useSyncExternalStore`'s `getSnapshot` (App.tsx's
// `useSessionClaim`), which requires a STABLE reference across repeated
// calls for the same underlying value -- `Object.is`-different snapshots
// on every render, even with identical content, trip React's "maximum
// update depth exceeded" loop guard (reproduced directly: an uncached
// decode re-parsed the JWT, and therefore returned a fresh object, on
// every single render). This single-entry cache, keyed by the exact token
// string, means repeated calls with the SAME current token return the
// SAME decoded object and the same `claim` reference; a genuinely
// different token (a real sign-in, redeem, refresh or sign-out) always
// misses the cache and decodes fresh.
let cachedPayloadToken: string | null = null;
let cachedPayload: Record<string, unknown> | null = null;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  if (cachedPayloadToken !== token) {
    cachedPayloadToken = token;
    cachedPayload = decodeJwtPayloadUncached(token);
  }
  return cachedPayload;
}

/**
 * The session JWT's one application-defined property (session_token.py's
 * own doc comment: "The JWT's only application-defined property is
 * `claim`"), forwarded exactly as the server sent it -- this module reads
 * no field inside it and names none of the capability claim contract's
 * own field names, so it carries none of `capability/claim.ts`'s
 * containment obligations; `capability/session-claim-source.ts` is the
 * only place that casts this to the capability boundary's own claim type,
 * and `CapabilityProvider` (via `decodeClaim`) is the only place that
 * validates it. Returns null with no session, or a malformed token.
 */
export function getSessionClaim(): unknown {
  const token = getSessionToken();
  if (token === null) {
    return null;
  }
  const payload = decodeJwtPayload(token);
  if (payload === null) {
    return null;
  }
  return "claim" in payload ? payload.claim : null;
}

/**
 * The session id (`sid`) -- session plumbing, not part of the published
 * claim contract (session_token.py's own doc comment), used only as the
 * sessionStorage sentinel key for the session-ended view's once-per-
 * session rule (ADR-20261015-6576, authenticated-fetch.ts). Never shown
 * to the customer and never sent back to the server by this module.
 */
export function getSessionId(): string | null {
  const token = getSessionToken();
  if (token === null) {
    return null;
  }
  const payload = decodeJwtPayload(token);
  const sid = payload?.sid;
  return typeof sid === "string" || typeof sid === "number" ? String(sid) : null;
}
